'use client';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { translations, type Lang, type Translations } from './i18n';

const LANG_STORAGE_KEY = 'docalert_lang';

interface LangContextType {
  lang: Lang;
  t: Translations;
  toggleLang: () => void;
  dir: 'ltr' | 'rtl';
  mounted: boolean;
}

const LangContext = createContext<LangContextType>({
  lang: 'en',
  t: translations.en,
  toggleLang: () => {},
  dir: 'ltr',
  mounted: false,
});

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>('en');
  const [mounted, setMounted] = useState(false);

  // On mount, read persisted language from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LANG_STORAGE_KEY) as Lang | null;
      if (stored === 'ar' || stored === 'en') {
        setLang(stored);
      }
    } catch {
      // localStorage not available
    }
    setMounted(true);
  }, []);

  // Apply dir and lang attributes to <html> element whenever lang changes
  useEffect(() => {
    if (!mounted) return;
    const dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', lang);
  }, [lang, mounted]);

  const toggleLang = useCallback(() => {
    setLang(prev => {
      const next: Lang = prev === 'en' ? 'ar' : 'en';
      try {
        localStorage.setItem(LANG_STORAGE_KEY, next);
      } catch {
        // localStorage not available
      }
      return next;
    });
  }, []);

  // Always use 'en' until mounted to ensure SSR and initial client render are identical.
  // After mount, switch to the stored language from localStorage.
  const activeLang: Lang = mounted ? lang : 'en';
  const t = translations[activeLang];
  const dir: 'ltr' | 'rtl' = activeLang === 'ar' ? 'rtl' : 'ltr';

  return (
    <LangContext.Provider value={{ lang: activeLang, t, toggleLang, dir, mounted }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}